import React, { useState, useRef, useEffect } from "react";
import { Typography, Box, TextField, Grid, Link } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { resentOTP, verifyOTP } from "../apiCalls";
import { toast } from "react-toast";

export default function OTP({ handleSubmit, otpId, setAttemptsExceeded, setIsLoading }) {
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const otpInputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];
  const [verificationError, setVerificationError] = useState("");
  const [timer, setTimer] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  const submitOtp = async () => {
    try {
      setIsLoading(true);
      const otp = otpValues.join("");
      const response = await verifyOTP(otpId, otp);
      if (response.success) {
        await handleSubmit(otpId);
      } else {
        setVerificationError("Invalid OTP");
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtp = async () => {
    if (isResendDisabled) return;

    try {
      setIsLoading(true);
      const response = await resentOTP(otpId);
      if (response.success) {
        if(response.attemptsRemaining <= 0){
          toast.error("Failed to resend OTP");
          setAttemptsExceeded(true);
        } else {
          toast.success("OTP resent successfully");
          setTimer(30);
          setIsResendDisabled(true);
        }
      } else {
        toast.error("Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0 && isResendDisabled) {
      setIsResendDisabled(false);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  return (
    <>
      <Typography variant="subtitle1" align="center" gutterBottom>
        OTP Verification
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{ mb: 3 }}
      >
        We've sent a verification code to the customer's registered mobile
        number.
      </Typography>

      <Box sx={{ maxWidth: 400, mx: "auto" }}>
        <Grid container spacing={2} justifyContent="center">
          {/* OTP Input Fields */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <TextField
                  key={index}
                  variant="outlined"
                  value={otpValues[index]}
                  inputRef={otpInputRefs[index]}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: "center", fontSize: "1.5rem" },
                  }}
                  sx={{
                    width: 60,
                    mx: 0.5,
                  }}
                  autoFocus={index === 0}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value && !/^[0-9]+$/.test(value)) return;

                    // Update the OTP value
                    const newOtpValues = [...otpValues];
                    newOtpValues[index] = value;
                    setOtpValues(newOtpValues);

                    // Move to next input if a digit is entered
                    if (value && index < 5) {
                      otpInputRefs[index + 1].current.focus();
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace
                    if (e.key === "Backspace") {
                      if (index > 0 && otpValues[index] === "") {
                        // If current field is empty and backspace is pressed, move to previous field
                        const newOtpValues = [...otpValues];
                        newOtpValues[index - 1] = "";
                        setOtpValues(newOtpValues);
                        otpInputRefs[index - 1].current.focus();
                      } else if (otpValues[index] !== "") {
                        // If current field has a value, clear it first
                        const newOtpValues = [...otpValues];
                        newOtpValues[index] = "";
                        setOtpValues(newOtpValues);
                      }
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasteData = e.clipboardData.getData("text");
                    const digits = pasteData
                      .replace(/[^0-9]/g, "")
                      .slice(0, 6)
                      .split("");

                    // Fill in as many fields as we have digits
                    const newOtpValues = [...otpValues];
                    digits.forEach((digit, i) => {
                      if (index + i < 6) {
                        newOtpValues[index + i] = digit;
                      }
                    });

                    setOtpValues(newOtpValues);

                    // Focus the next empty field or the last field
                    const nextEmptyIndex = newOtpValues.findIndex(
                      (val) => val === ""
                    );
                    if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
                      otpInputRefs[nextEmptyIndex].current.focus();
                    } else {
                      otpInputRefs[5].current.focus();
                    }
                  }}
                />
              ))}
            </Box>
          </Grid>
          {verificationError && (
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <ErrorOutlineIcon sx={{ color: "error.main", mr: 1, fontSize: "1rem" }} />
              <Typography variant="body2" color="error">
                {verificationError}
              </Typography>
            </Grid>
          )}

          {/* Timer and Resend */}
          <Grid item xs={12} sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Didn't receive OTP?{" "}
              <Link 
                component="button" 
                underline="hover" 
                onClick={() => resendOtp()}
                sx={{ 
                  opacity: isResendDisabled ? 0.6 : 1,
                  pointerEvents: isResendDisabled ? "none" : "auto"
                }}
              >
                {isResendDisabled ? `Resend OTP (${timer}s)` : "Resend OTP"}
              </Link>
            </Typography>
          </Grid>

          {/* Verify Button */}
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <button
              className={
                otpValues[5] === "" ||
                otpValues[4] === "" ||
                otpValues[3] === "" ||
                otpValues[2] === "" ||
                otpValues[1] === "" ||
                otpValues[0] === ""
                  ? "border-gray-500 bg-gray-500 text-gray-100 border cursor-default rounded-md w-60 p-1 text-sm font-semibold"
                  : "border-blue-600 bg-blue-600 text-gray-100 border cursor-pointer rounded-md w-60 p-1 text-sm font-semibold"
              }
              onClick={() => submitOtp()}
            >
              Verify OTP
            </button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
